import { sileo } from "sileo";
import { mensajeDeError } from "../mensajeDeError";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateChangelogEntry,
  useUpdateChangelogEntry,
} from "../hooks/useChangelog";
import type {
  ChangelogEntry,
  ChangelogKind,
  CreateChangelogEntryData,
  UpdateChangelogEntryData,
} from "../types";

interface ChangelogEditModalProps {
  open: boolean;
  entry?: ChangelogEntry | null;
  onClose: () => void;
}

const KIND_VALUES: ChangelogKind[] = [
  "feature",
  "improvement",
  "fix",
  "breaking",
  "security",
];

const KIND_LABELS: Record<ChangelogKind, string> = {
  feature: "Nueva funcionalidad",
  improvement: "Mejora",
  fix: "Corrección",
  breaking: "Cambio incompatible",
  security: "Seguridad",
};

const tagsToString = (tags: string[] | undefined) => (tags ?? []).join(", ");

const stringToTags = (value: string): string[] =>
  value
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);

const ChangelogEditModal = ({
  open,
  entry,
  onClose,
}: ChangelogEditModalProps) => {
  const isEdit = !!entry;

  const [version, setVersion] = useState(entry?.version ?? "");
  const [title, setTitle] = useState(entry?.title ?? "");
  const [kind, setKind] = useState<ChangelogKind>(entry?.kind ?? "feature");
  const [tagsInput, setTagsInput] = useState(tagsToString(entry?.tags));
  const [body, setBody] = useState(entry?.body ?? "");
  const [error, setError] = useState("");

  const createMutation = useCreateChangelogEntry();
  const updateMutation = useUpdateChangelogEntry();

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!version.trim()) {
      setError("La versión es requerida");
      return;
    }
    if (!title.trim()) {
      setError("El título es requerido");
      return;
    }
    if (!body.trim()) {
      setError("El cuerpo (markdown) es requerido");
      return;
    }

    const tags = stringToTags(tagsInput);

    try {
      if (isEdit && entry) {
        const payload: UpdateChangelogEntryData = {
          version: version.trim(),
          title: title.trim(),
          body,
          kind,
          tags,
        };
        await updateMutation.mutateAsync({ id: entry.id, data: payload });
      } else {
        const payload: CreateChangelogEntryData = {
          version: version.trim(),
          title: title.trim(),
          body,
          kind,
          tags,
        };
        await createMutation.mutateAsync(payload);
      }

      // Confirmar siempre. Una entrada nueva nace como borrador, así que sin este aviso el modal
      // se cerraba sin más y no había manera de distinguir "se guardó" de "no pasó nada": el admin
      // la volvía a crear y quedaban duplicados.
      sileo.success({
        title: isEdit ? "Entrada actualizada" : "Entrada creada como borrador",
        description: `Versión ${version.trim()}`,
      });

      onClose();
    } catch (err: unknown) {
      setError(mensajeDeError(err, "Error al guardar la entrada"));
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !isPending) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl border-neutral-800 bg-neutral-900">
        <DialogHeader>
          <DialogTitle className="text-neutral-100">
            {isEdit
              ? "Editar entrada del changelog"
              : "Nueva entrada del changelog"}
          </DialogTitle>
          <DialogDescription className="text-neutral-400">
            {isEdit
              ? "Modifica los campos y guarda los cambios."
              : "Crea una nueva entrada en estado borrador. Podrás publicarla luego."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-300">
                Versión *
              </label>
              <Input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="1.4.0"
                disabled={isPending}
                className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-300">
                Tipo *
              </label>
              <Select
                value={kind}
                onValueChange={(value) => setKind(value as ChangelogKind)}
                disabled={isPending}
              >
                <SelectTrigger className="border-neutral-700 bg-neutral-800/50 text-neutral-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-neutral-700 bg-neutral-900 text-neutral-200">
                  {KIND_VALUES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {KIND_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-300">
              Título *
            </label>
            <Input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Resumen breve del cambio"
              disabled={isPending}
              className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-300">
              Etiquetas
            </label>
            <Input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="dashboard, panels, performance"
              disabled={isPending}
              className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
            />
            <p className="mt-1 text-xs text-neutral-500">
              Separadas por coma. Se guardan como un array de strings.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col">
              <label className="mb-1 block text-sm font-medium text-neutral-300">
                Cuerpo (Markdown) *
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="## Highlights&#10;- Item 1&#10;- Item 2"
                rows={14}
                disabled={isPending}
                className="w-full rounded-md border border-neutral-700 bg-neutral-800/50 px-3 py-2 font-mono text-sm text-neutral-200 outline-none transition focus:border-purple-500"
              />
            </div>

            <div className="flex flex-col">
              <label className="mb-1 block text-sm font-medium text-neutral-300">
                Vista previa
              </label>
              <div className="min-h-[14rem] flex-1 overflow-auto rounded-md border border-dashed border-neutral-700 bg-neutral-900/40 p-3 text-sm text-neutral-400">
                Vista previa pendiente — markdown will render on the public
                site.
                <pre className="mt-3 whitespace-pre-wrap font-mono text-xs text-neutral-500">
                  {body || "(vacío)"}
                </pre>
              </div>
            </div>
          </div>

          {error ? (
            <div className="rounded-md bg-red-900/30 px-4 py-2 text-sm text-red-400">
              {error}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
              className="border-neutral-700 bg-transparent text-neutral-300 hover:bg-neutral-800 hover:text-neutral-100"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              {isPending
                ? "Guardando..."
                : isEdit
                  ? "Guardar cambios"
                  : "Crear entrada"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangelogEditModal;
